import { lazy, type ComponentType } from 'react';

const RELOAD_KEY = 'lazyRetry_reloaded';

/**
 * Wrapper around React.lazy that retries on chunk load failure.
 * On the first failure it clears caches and reloads the page once.
 * Prevents infinite reload loops via sessionStorage flag.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch(async (error: unknown) => {
      const alreadyRetried = sessionStorage.getItem(RELOAD_KEY);
      if (!alreadyRetried) {
        sessionStorage.setItem(RELOAD_KEY, '1');

        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }

        window.location.reload();
        // Return a never-resolving promise so React doesn't render stale state
        return new Promise<{ default: T }>(() => {});
      }

      sessionStorage.removeItem(RELOAD_KEY);
      throw error;
    }),
  );
}

// Clear the retry flag on successful page load
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    sessionStorage.removeItem(RELOAD_KEY);
  });
}
